import { BuildSettingsKey } from "@builder/store/slices/buildSettingsKeys/buildSettingsKeySlice";
import { getAuthToken } from "jwt-storage-lib";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const buildSettingsKeyApi = createApi({
    reducerPath: "buildSettingsKeyApi",
    baseQuery: fetchBaseQuery({
        baseUrl: import.meta.env.VITE_BASE_API_URL,
        prepareHeaders: async headers => {
            const token = await getAuthToken();

            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }

            return headers;
        }
    }),
    tagTypes: ["BuildSettingsKeys"],
    endpoints: builder => ({
        getBuildSettingsKeys: builder.query<{ [key: string]: BuildSettingsKey }, { companyId: string; buildId: string }>({
            query: query => `v1/api/build-settings-key/${query.companyId}?companyId=${query.companyId}&buildId=${query.buildId}`,
            transformResponse: (response: BuildSettingsKey[]) => {
                return response.reduce((acc: { [key: string]: BuildSettingsKey }, bsk: BuildSettingsKey) => {
                    if (bsk.key) {
                        acc[bsk.key] = bsk;
                    }
                    return acc;
                }, {});
            },
            providesTags: ["BuildSettingsKeys"]
        }),
        createBuildSettingsKey: builder.mutation<BuildSettingsKey, Partial<BuildSettingsKey>>({
            query: ({ ...buildSettingsKey }) => ({
                url: `v1/api/build-settings-key/${buildSettingsKey.companyId}`,
                method: "POST",
                body: buildSettingsKey
            }),
            invalidatesTags: ["BuildSettingsKeys"]
        }),
        updateBuildSettingsKey: builder.mutation<BuildSettingsKey, { id: string; buildSettingsKey: Partial<BuildSettingsKey> }>({
            query: query => ({
                url: `v1/api/build-settings-key/${query.buildSettingsKey.companyId}/${query.id}`,
                method: "PATCH",
                body: query.buildSettingsKey
            }),
            invalidatesTags: ["BuildSettingsKeys"]
        }),
        deleteBuildSettingsKey: builder.mutation<void, { id: string; companyId: string }>({
            query: ({ id, companyId }) => ({
                url: `v1/api/build-settings-key/${companyId}/${id}`,
                method: "DELETE"
            }),
            invalidatesTags: ["BuildSettingsKeys"]
        }),
        uploadImage: builder.mutation<any, { companyId: string; accountId?: string; file: File }>({
            query: ({ companyId, accountId, file }) => {
                const formData = new FormData();
                formData.append("companyId", companyId);
                if (accountId) formData.append("accountId", accountId);
                formData.append("files", file);
                return {
                    url: `v1/api/attachments/${companyId}`,
                    method: "POST",
                    body: formData
                };
            }
        })
    })
});

export const {
    useGetBuildSettingsKeysQuery,
    useCreateBuildSettingsKeyMutation,
    useUpdateBuildSettingsKeyMutation,
    useUploadImageMutation,
    useDeleteBuildSettingsKeyMutation
} = buildSettingsKeyApi;
